import React, { useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
  modalConfirm,
  modalQuestionResolution,
  modalHint,
  modalHintShow,
} from "../../utils/logger";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-violet.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaViolet from "../../assets/btnPistaViolet.png";
import imageHint from "../../assets/hints/imageHintViolet.png";
import imageHintUsed from "../../assets/btnPistaViolet.png";
import unIntento from "../../assets/un-intento-violet.png";
import dosIntentos from "../../assets/dos-intentos-violet.png";

import imgAVCH from "../../assets/tests/scn2/alto-volumen-chaarted.png";
import imgARLAT from "../../assets/tests/scn2/alto-riesgo-latitude.png";
import imgAV from "../../assets/tests/scn2/alto-volumen.png";
import imgAR from "../../assets/tests/scn2/alto-riesgo.png";

function Scenario3Quiz3({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const QUIZ_ID = QUIZZES.QUIZ6;
  const SCENARIO_ID = SCENARIOS.SCENARIO_3;

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [attempt, setAttempt] = useState(1);
  const [blockedOptions, setBlockedOptions] = useState(new Set());
  const [pendingScore, setPendingScore] = useState(0);

  const NUMBERATTEMPTS = 2;
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  const options = [
    {
      id: 1,
      text: "Al menos 1 de los siguientes criterios:<ul><li>4 o más metástasis óseas</li><li>(1 fuera de la pelvis/columna)</li><li>Metástasis viscerales</li></ul>",
      img: imgAVCH,
      correct: true,
      explanation: "-----",
    },
    {
      id: 2,
      text: "Al menos 2 de los siguientes criterios:<ul><li>3 o más metástasis óseas</li><li>Metástasis viscerales</li><li>Gleason mayor o igual a 8</li></ul>",
      img: imgARLAT,
      correct: true,
      explanation: "-----",
    },
    {
      id: 3,
      text: "Más de 10 metástasis óseas</br>Metástasis locorregionales",
      img: imgAV,
      correct: false,
      explanation: "-----",
    },
    {
      id: 4,
      text: "PSA mayor o igual a 20</br>Gleason mayor o igual a 8 o T3a</br>PSADT menor o igual a 10 meses",
      img: imgAR,
      correct: false,
      explanation: "-----",
    },
  ];

  const correctIds = options.filter((o) => o.correct).map((o) => o.id);

  const toggleOption = (id) => {
    if (blockedOptions.has(id)) return;
    setSelectedOptions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  async function submitAnswer(finalScore, correctFinal) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));

    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore,
        time: totalTime,
        lastVisited: ROUTES.SCENARIO3_FIN,
        correct: correctFinal,
      });

      if (res.status === 201) {
        const serverGroup = res.data?.group;
        if (serverGroup) {
          dispatch({ type: "GROUP_SET", payload: serverGroup });
        }
        setIsCompleted(true);
        navigate(ROUTES.SCENARIO3_FIN);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        await modalQuestionResolution({
          correct: false,
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });
        navigate(ROUTES.SCENARIO3_FIN);
        return;
      }

      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });

      setIsSubmitting(false);
    }
  }

  const useHint = async () => {
    const hint =
      "Los criterios correctos para definir el volumen y el riesgo fueron establecidos por dos ensayos clínicos cuyos nombres hacen referencia a características geográficas y astronómicas. Fíjate en las definiciones que incluyen un número concreto de metástasis y la presencia de enfermedad visceral.";
    if (hintUsed) {
      await modalHintShow({ hint, imageHintUsed, color: "violet", big: false });
      return;
    }
    const { used } = await modalHint({
      image: imageHint,
      imageHintUsed,
      hint,
      color: "violet",
      big: false,
    });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3);
    }
  };

  function checkAnswer() {
    if (selectedOptions.length === 0) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="¿Estás seguro?" class='estasseguro' /><div class="text-center txtTitulos mt-3 violet">Deben seleccionar la opción u opciones que consideren correctas</div>`,
      });
      return;
    }

    const isCorrect =
      correctIds.length === selectedOptions.length &&
      correctIds.every((id) => selectedOptions.includes(id));

    modalConfirm({ estasseguro, color: "violet" }).then((res) => {
      if (!res.isConfirmed) return;

      setIsSubmitting(true);
      const hintThisAttempt =
        hintUsed && !hintAppliedToScore ? hintPenalty : 0;

      if (isCorrect) {
        const base =
          attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const attemptDelta = base - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: "Sus respuestas",
          textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:450px' />
                     <div class="txt28 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Continuar",
          color: "violet",
        }).then(() => {
          submitAnswer(newScore, true);
        });

        return;
      }

      if (attempt < NUMBERATTEMPTS) {
        const wrongSelected = selectedOptions.filter(
          (id) => !correctIds.includes(id)
        );
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: "Sus respuestas",
          textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                     <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Les queda <b>1</b> intento. Elijan de nuevo.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "violet",
        }).then(() => {
          setPendingScore(newScore);
          setBlockedOptions((prev) => {
            const next = new Set(prev);
            wrongSelected.forEach((id) => next.add(id));
            return next;
          });
          setAttempt(attempt + 1);
          setSelectedOptions([]);
          setIsSubmitting(false);
        });

        return;
      }

      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt;
      const newScore = pendingScore + attemptDelta;
      if (hintThisAttempt) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: "Sus respuestas",
        textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                   <div class="txt28 bold pt-4">Han agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
        customConfirmButtonHtml: "Continuar",
        color: "violet",
      }).then(() => {
        submitAnswer(newScore, false);
      });
    });
  }

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

  const shuffledOptions = useMemo(() => {
    return [...options].sort(() => Math.random() - 0.5);
  }, []);

  const VIOLET = "#8c3888";

  return (
    <div className="h-100">
      <div className="container-fluid px-5 h-100 d-flex flex-column justify-content-start pt-3 bgCircleViolet">
        <h3 className="txt24 txt-white text-center" style={{ backgroundColor: "#8c3888", borderRadius: "10px", padding: "12px"}}>
          A partir de la información previa, ¿podrían decir cómo se define enfermedad de alto volumen y de alto riesgo?
        </h3>
        <div className="d-flex justify-content-between">
          <h5 className="mb-4 txt24 text-left my-2 gray">
            <IconHandClick size={40} color="#757575" stroke={1} /> Selección múltiple, seleccionen las respuestas correctas
          </h5>
          <div className="mb-3">
            <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
          </div>
        </div>
        <div className="row g-4">
          {shuffledOptions.map((option) => {
            const isSelected = selectedOptions.includes(option.id);
            const isBlocked = blockedOptions.has(option.id);
            return (
              <div key={option.id} className="col-12 col-md-6 col-lg-3">
                <div
                  className={`position-relative cursor-pointer p-1 rounded shadow h-75 ${isSelected ? "selected" : ""} ${isBlocked ? "opacity-75" : ""}`}
                  style={{
                    border: isSelected
                      ? "1px solid #8c388799"
                      : "1px solid #fff",
                    backgroundColor: isSelected ? "#8c388855" : "#fff",
                    pointerEvents: isBlocked ? "none" : "auto",
                  }}
                  onClick={() => toggleOption(option.id)}
                  aria-disabled={isBlocked}
                  role="button"
                  tabIndex={isBlocked ? -1 : 0}
                  onKeyDown={(e) => {
                    if (isBlocked) return;
                    if (e.key === "Enter" || e.key === " ")
                      toggleOption(option.id);
                  }}
                  title={
                    isBlocked
                      ? "Opción bloqueada (ya fue incorrecta)"
                      : "Seleccionar"
                  }
                >
                  <img
                    src={option.img}
                    alt={`Opción ${option.id}`}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      height: "40%",
                      objectFit: "contain",
                    }}
                  />
                  <div
                    className="txt15 px-1 mt-1 gray"
                    dangerouslySetInnerHTML={{ __html: option.text }}
                  />
                  {isBlocked && (
                    <span className="badge bg-secondary position-absolute top-0 end-0 m-2">
                      Bloqueada
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-between" style={{"marginTop":"-40px"}}>
          <div className="position-relative d-inline-block">
            <img
              src={btnPistaViolet}
              className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
              onClick={useHint}
              alt={hintUsed ? "Pista usada" : "Usar pista"}
              title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
            />
            {hintUsed && <span className="badge-penalty">-3</span>}
          </div>
          <CheckButton
            text="Comprobar respuesta"
            onClick={checkAnswer}
            disabled={isCompleted || isSubmitting}
            color="violet"
            loading={isSubmitting}
          />
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario3Quiz3);
